// Birim varyantı DR10 — MetricModal'ın birim bölümü (MetricModal 600 satır sınırında, ayrı dosya):
// varsayılan birim alanı + "Alternatif birimler" çipleri (UnitVariantsField) + türetilmişte şablon
// yardımı + mevcut metrikte varsayılan değişince müşteri bazında kullanım (`getKpiMetricUnitUsage`,
// 300 ms debounce, son istek kazanır) → `UnitChangeBand variant="catalog"`. Kullanım varken (ya da
// denetlenirken) Kaydet pasif — üst bileşene `onBlockedChange`. Yeni metrikte kullanım çağrısı yok.

import { useEffect, useState } from "react";
import type { KpiUnitUsage } from "@mahir/shared";
import { getKpiMetricUnitUsage } from "../../api/kpi";
import { KPI_UNIT_COPY } from "../../lib/kpi-unit-copy";
import { sameUnitPick, UNIT_PREVIEW_DEBOUNCE_MS } from "../../lib/kpi-unit-change-flow";
import { canSaveCatalog } from "../../lib/kpi-unit-band";
import { UnitChangeBand } from "./UnitChangeBand";
import { UnitCombobox } from "./UnitCombobox";
import { UnitVariantsField } from "./UnitVariantsField";
import { fieldStyle, helpText, inputStyle, labelStyle } from "./kpi-form-styles";

const C = KPI_UNIT_COPY.catalog;

export interface CatalogUsageState {
  loading: boolean;
  error: boolean;
  usage: KpiUnitUsage | null;
}
const IDLE: CatalogUsageState = { loading: false, error: false, usage: null };

/** Mevcut metrikte varsayılan birim değişti mi (normalize; boş = birimsiz). */
export function catalogDefaultChanged(
  metricId: string | null,
  original: string | null,
  next: string,
): boolean {
  return metricId !== null && !sameUnitPick(next.trim() || null, original);
}

/** Kaydet kapısı: değişimde kullanım denetlenirken ya da kullanım varken pasif (hata → sunucu kapısı). */
export function catalogUnitBlocked(changed: boolean, s: CatalogUsageState): boolean {
  if (!changed) return false;
  if (s.loading) return true;
  if (s.error) return false;
  return !canSaveCatalog(s.usage);
}

export interface MetricUnitSectionViewProps {
  defaultUnit: string;
  unitVariants: readonly string[];
  isDerived: boolean;
  disabled: boolean;
  changed: boolean;
  usage: CatalogUsageState;
  onDefaultUnit: (v: string) => void;
  onVariants: (v: string[]) => void;
  onRecheck: () => void;
  onOpenClient: (clientId: string, setId?: string | null) => void;
}

export function MetricUnitSectionView(props: MetricUnitSectionViewProps): JSX.Element {
  const { usage } = props;
  return (
    <>
      <div style={fieldStyle}>
        <label style={labelStyle} htmlFor="m-unit">
          {C.defaultLabel}
        </label>
        <span style={helpText}>{C.defaultHelp}</span>
        {props.isDerived ? (
          <input
            id="m-unit"
            style={{ ...inputStyle, maxWidth: 260 }}
            value={props.defaultUnit}
            disabled={props.disabled}
            maxLength={32}
            placeholder="GJ, tCO₂e, %…"
            onChange={(e) => props.onDefaultUnit(e.target.value)}
          />
        ) : (
          <UnitCombobox
            id="m-unit"
            ariaLabel={C.defaultLabel}
            value={props.defaultUnit}
            onChange={props.onDefaultUnit}
            placeholder={C.defaultPlaceholder}
            {...(props.disabled ? { disabled: true } : {})}
          />
        )}
        {props.isDerived ? <span style={helpText}>{C.derivedHelp}</span> : null}
        {props.changed ? (
          <UnitChangeBand
            preview={null}
            loading={usage.loading}
            error={usage.error}
            mode={null}
            onMode={() => undefined}
            onRecheck={props.onRecheck}
            clientName=""
            variant="catalog"
            catalogUsage={usage.usage}
            onOpenClient={props.onOpenClient}
          />
        ) : null}
      </div>
      {props.isDerived ? null : (
        <UnitVariantsField
          value={props.unitVariants}
          defaultUnit={props.defaultUnit}
          disabled={props.disabled}
          onChange={props.onVariants}
        />
      )}
    </>
  );
}

export interface MetricUnitSectionProps {
  /** null = yeni metrik (kullanım çağrısı yok) */
  metricId: string | null;
  originalDefaultUnit: string | null;
  defaultUnit: string;
  unitVariants: readonly string[];
  isDerived: boolean;
  disabled: boolean;
  onDefaultUnit: (v: string) => void;
  onVariants: (v: string[]) => void;
  onBlockedChange: (blocked: boolean) => void;
  onOpenClient: (clientId: string, setId?: string | null) => void;
}

export function MetricUnitSection(props: MetricUnitSectionProps): JSX.Element {
  const { metricId, originalDefaultUnit, defaultUnit, onBlockedChange } = props;
  const [usage, setUsage] = useState<CatalogUsageState>(IDLE);
  const [nonce, setNonce] = useState(0);
  const changed = catalogDefaultChanged(metricId, originalDefaultUnit, defaultUnit);

  useEffect(() => {
    if (!changed || metricId === null) {
      setUsage(IDLE);
      return;
    }
    let live = true;
    setUsage({ loading: true, error: false, usage: null });
    const t = setTimeout(() => {
      getKpiMetricUnitUsage(metricId).then(
        (u) => {
          if (live) setUsage({ loading: false, error: false, usage: u });
        },
        () => {
          if (live) setUsage({ loading: false, error: true, usage: null });
        },
      );
    }, UNIT_PREVIEW_DEBOUNCE_MS);
    return () => {
      live = false;
      clearTimeout(t);
    };
    // defaultUnit: her yazımda yeniden denetle (son istek kazanır); nonce: "Yeniden dene"
  }, [changed, metricId, defaultUnit, nonce]);

  const blocked = catalogUnitBlocked(changed, usage);
  useEffect(() => {
    onBlockedChange(blocked);
  }, [blocked, onBlockedChange]);

  return (
    <MetricUnitSectionView
      defaultUnit={defaultUnit}
      unitVariants={props.unitVariants}
      isDerived={props.isDerived}
      disabled={props.disabled}
      changed={changed}
      usage={usage}
      onDefaultUnit={props.onDefaultUnit}
      onVariants={props.onVariants}
      onRecheck={() => setNonce((n) => n + 1)}
      onOpenClient={props.onOpenClient}
    />
  );
}
